import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { isoWeekLabel } from "../lib/isoWeek.js";
import { AsyncButton } from "./ui/async-button.js";
import { Textarea } from "./ui/textarea.js";
import { cn } from "../lib/utils.js";

export interface WeeklyHoursCorrectionRow {
  id: string;
  weekStart: string;
  requestedTotalHours: number;
  reason: string;
  createdAt: string;
  totalHours: number;
  expectedHours: number;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
}

interface Props {
  requests: WeeklyHoursCorrectionRow[];
  onRefresh: () => void;
}

/** The employee's own words for why the correction was raised. Given its own
 * labelled row so it reads as part of the request, not as a caption. */
function ReasonRow({ reason }: { reason: string }) {
  return (
    <div className="flex items-baseline gap-2 border-t border-[var(--color-surface-200)] px-3 py-1.5">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">Reason</span>
      {/* Wraps instead of clamping: a one-word reason keeps the row one line
          high, a long one grows the box to fit rather than hiding text. */}
      <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] text-[var(--color-surface-800)]">
        {reason}
      </span>
    </div>
  );
}

/** One half of the before/after box: a label over an hours figure. */
function HoursPair({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2 px-3 py-1.5">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">{label}</span>
      <span className={cn("truncate text-[13px] font-medium", emphasis ? "text-[var(--color-brand-600)]" : "text-[var(--color-surface-800)]")}>
        {value}
      </span>
    </div>
  );
}

/** Approver queue for weekly hours corrections. These are raised against a past
 * week, so unlike the week-scoped sign-off queue every row carries its own week
 * label and the list spans all weeks. */
export function WeeklyHoursCorrectionQueue({ requests, onRefresh }: Props) {
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  async function doApprove(id: string) {
    setErr("");
    try {
      const remark = (remarks[id] ?? "").trim();
      await api.patch(`/attendance/weekly-hours-corrections/${id}/approve`, remark ? { approverRemark: remark } : {});
      onRefresh();
    } catch (e) {
      logError("WeeklyHoursCorrectionQueue.doApprove", e);
      setErr("Failed to approve — try again");
      throw e;
    }
  }

  async function doReject(id: string) {
    const remark = (remarks[id] ?? "").trim();
    if (!remark) {
      setErr("Enter a remark for the decline");
      throw new Error("validation");
    }
    setErr("");
    try {
      await api.patch(`/attendance/weekly-hours-corrections/${id}/reject`, { approverRemark: remark });
      onRefresh();
    } catch (e) {
      logError("WeeklyHoursCorrectionQueue.doReject", e);
      setErr("Failed to decline — try again");
      throw e;
    }
  }

  if (!requests.length) {
    return (
      <div className="py-5 text-center text-[13px] text-[var(--color-surface-400)]">
        No pending weekly hours corrections
      </div>
    );
  }

  return (
    <div>
      {err && (
        <div className="mb-2.5 rounded-lg bg-[var(--color-error-bg)] px-3 py-2 text-[13px] text-[var(--color-error-fg)]">
          {err}
        </div>
      )}

      {requests.map((r) => {
        const expanded = expandedId === r.id;
        const short = r.totalHours < r.expectedHours - 0.01;
        return (
          <div
            key={r.id}
            className="mb-2 overflow-hidden rounded-[10px] border border-[var(--color-surface-200)] bg-[var(--color-surface-0)]"
          >
            <button
              onClick={() => setExpandedId(expanded ? null : r.id)}
              className="flex w-full items-center justify-between gap-2 border-none bg-transparent px-3.5 py-2.5 text-left"
            >
              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                {expanded
                  ? <ChevronDown size={14} className="shrink-0 text-[var(--color-surface-500)]" />
                  : <ChevronRight size={14} className="shrink-0 text-[var(--color-surface-500)]" />}
                <span className="truncate text-sm font-semibold text-[var(--color-surface-900)]">
                  {r.employee.firstName} {r.employee.lastName}
                </span>
                <span className="shrink-0 text-xs text-[var(--color-surface-400)]">{r.employee.employeeCode}</span>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className={`text-[13px] font-semibold ${short ? "text-[var(--color-error)]" : "text-[var(--color-surface-800)]"}`}>
                  {r.totalHours.toFixed(1)} / {r.expectedHours.toFixed(1)} hrs
                </span>
                <span className="text-xs text-[var(--color-surface-500)]">{isoWeekLabel(r.weekStart)}</span>
              </div>
            </button>

            {expanded && (
              <div className="border-t border-[var(--color-surface-100)] px-3.5 pb-3 pt-2.5">
                {/* Recorded and requested hours sit side by side in one box, so the
                    before/after comparison reads across instead of down the card. */}
                <div className="mb-2 overflow-hidden rounded-lg border border-[var(--color-surface-200)] bg-[var(--color-surface-50)]">
                  <div className="grid grid-cols-2 divide-x divide-[var(--color-surface-200)]">
                    <HoursPair label="Recorded" value={`${r.totalHours.toFixed(1)} / ${r.expectedHours.toFixed(1)} hrs`} />
                    <HoursPair label="Requested" value={`${r.requestedTotalHours.toFixed(1)} hrs`} emphasis />
                  </div>
                  <ReasonRow reason={r.reason} />
                </div>

                {/* Remark and the decision buttons share one row to save height. */}
                <div className="flex items-start gap-2">
                  <Textarea
                    autoGrow
                    minRows={1}
                    maxRows={5}
                    size="sm"
                    aria-label="Approval remark"
                    placeholder="Add a remark (optional to approve, required to decline)…"
                    value={remarks[r.id] ?? ""}
                    onChange={(e) => setRemarks((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    className="min-w-0 flex-1"
                  />
                  <AsyncButton size="sm" variant="success" onClick={() => doApprove(r.id)}>Approve</AsyncButton>
                  <AsyncButton size="sm" variant="destructive" onClick={() => doReject(r.id)}>Decline</AsyncButton>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
